import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { gql, useQuery } from 'urql';
import PageHeader from '@/components/PageHeader';

const Q_CAJA_DASHBOARD = gql`
  query CajaDashboard($sucursalId: ID!) {
    cajaDashboard(sucursal_id: $sucursalId) {
      kpis { saldoTotal diferenciasDia cierresPendientes }
      cards {
        cajaId
        nombre
        saldo
        ingresosHoy
        egresosHoy
        cierrePendiente
        diferenciaHoy
        alertas
      }
    }
  }
`;

const money = (v) => new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(Number(v || 0));

export default function CajasPage() {
  const [sucursalId, setSucursalId] = useState('1');
  const [res, refetch] = useQuery({ query: Q_CAJA_DASHBOARD, variables: { sucursalId }, requestPolicy: 'network-only' });

  const data = res.data?.cajaDashboard;
  const cards = data?.cards || [];
  const kpis = data?.kpis || { saldoTotal: 0, diferenciasDia: 0, cierresPendientes: 0 };

  const totalAlertas = useMemo(() => cards.reduce((acc, c) => acc + (c.alertas?.length || 0), 0), [cards]);

  return (
    <div className="min-h-screen bg-[rgba(var(--bg))]">
      <PageHeader title="Cajas diarias" subtitle="Control de caja, arqueo, cierre y rendiciones por sucursal" />
      <section className="max-w-[1320px] mx-auto px-4 py-6 space-y-4">
        <div className="rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--panel))] p-4 flex gap-3 items-end">
          <label className="text-sm text-[rgb(var(--muted))]">
            Sucursal
            <input className="block mt-1 rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg))] px-3 py-2" value={sucursalId} onChange={(e) => setSucursalId(e.target.value)} />
          </label>
          <button type="button" onClick={() => refetch({ requestPolicy: 'network-only' })} className="rounded-xl border border-[rgb(var(--border))] px-3 py-2 hover:bg-[rgba(var(--muted),0.15)]">Actualizar</button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <article className="rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--panel))] p-4"><p className="text-xs text-[rgb(var(--muted))]">Saldo total en cajas</p><p className="text-xl font-semibold">{money(kpis.saldoTotal)}</p></article>
          <article className="rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--panel))] p-4"><p className="text-xs text-[rgb(var(--muted))]">Diferencias del día</p><p className="text-xl font-semibold">{money(kpis.diferenciasDia)}</p></article>
          <article className="rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--panel))] p-4"><p className="text-xs text-[rgb(var(--muted))]">Cierres pendientes</p><p className="text-xl font-semibold">{kpis.cierresPendientes}</p></article>
          <article className="rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--panel))] p-4"><p className="text-xs text-[rgb(var(--muted))]">Alertas activas</p><p className="text-xl font-semibold">{totalAlertas}</p></article>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {cards.map((card) => (
            <Link key={card.cajaId} to={`/tesoreria/cajas/${card.cajaId}`} className="rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--panel))] p-4 hover:border-[rgb(var(--brand))] transition-colors">
              <div className="flex items-start justify-between gap-2">
                <h3 className="font-semibold">{card.nombre}</h3>
                {card.cierrePendiente ? <span className="text-xs rounded-full px-2 py-1 bg-[rgba(var(--warn),0.2)]">Cierre pendiente</span> : null}
              </div>
              <p className="text-xs text-[rgb(var(--muted))] mt-2">Saldo hoy</p>
              <p className="text-lg font-semibold">{money(card.saldo)}</p>
              <div className="mt-3 text-sm text-[rgb(var(--muted))]">
                <p>Ingresos: {money(card.ingresosHoy)}</p>
                <p>Egresos: {money(card.egresosHoy)}</p>
              </div>
              {card.alertas?.length ? <p className="mt-3 text-xs text-[rgb(var(--danger))]">Alertas: {card.alertas.join(', ')}</p> : null}
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
